/* Edge Paint Selector — v3
   State is one Map of variantId -> { have, want, key }: `have` is what the cart holds
   (this product's plain lines), `want` is the list. Every view renders from `want`, so
   they can never disagree; the one button makes the cart match the list. */

(function () {
  'use strict';

  if (customElements.get('edge-paint-selector')) return;

  const KEY_SEP = '\u0001';
  const keyOf = (color, size) => color + KEY_SEP + size;
  const MOBILE = window.matchMedia('(max-width: 749px)');
  const { escapeHtml } = window.JLL;

  const splitName = (name) => {
    const m = /^(.*?)\s*\(([^)]*)\)\s*$/.exec(name);
    return m ? { name: m[1], code: m[2] } : { name, code: '' };
  };

  // "120ml" / "250 ml" / "1 L" / "5L" / "50 g" -> { n: millilitres|grams, u: 'ml'|'g' }
  const parseVolume = (label) => {
    const m = /(\d+(?:[.,]\d+)?)\s*(ml|l|g|kg)\b/i.exec(label || '');
    if (!m) return null;
    let n = parseFloat(m[1].replace(',', '.'));
    let u = m[2].toLowerCase();
    if (u === 'l') { n *= 1000; u = 'ml'; }
    if (u === 'kg') { n *= 1000; u = 'g'; }
    return { n, u };
  };

  const formatVolume = (n, u) => {
    const big = u === 'ml' ? 'L' : 'kg';
    if (n >= 1000) {
      const v = (n / 1000).toFixed(2).replace(/\.?0+$/, '');
      return `${v} ${big}`;
    }
    return `${Math.round(n)} ${u}`;
  };

  class EdgePaintSelector extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      JLL.tameEntrance(this);

      const data = this.querySelector('[data-eps-variants]');
      this.variants = data ? JSON.parse(data.textContent) : [];
      this.sizes = JSON.parse(this.dataset.sizes);
      this.formatMoney = JLL.money(this.dataset.moneySample);
      this.lowStock = parseInt(this.dataset.lowStock, 10);
      this.itemLabel = this.dataset.itemLabel;

      this.byId = new Map();
      this.byKey = new Map();
      this.variants.forEach((v) => {
        this.byId.set(v.id, v);
        this.byKey.set(keyOf(v.color, v.size), v);
      });

      this.list = new Map();
      this.openColor = null;

      this.tiles = Array.from(this.querySelectorAll('[data-eps-tile]'));
      this.colors = this.tiles.map((tile) => {
        const swatchEl = tile.querySelector('.eps-tile__swatch');
        const family = tile.closest('.eps-family');
        const label = family && family.querySelector('.eps-family__label');
        return {
          name: tile.dataset.color,
          swatch: swatchEl ? swatchEl.style.getPropertyValue('--eps-swatch').trim() : '',
          family: label ? label.textContent.trim() : '',
          tile,
        };
      });
      this.colorByName = new Map(this.colors.map((c) => [c.name, c]));

      this.els = {
        panel: this.querySelector('[data-eps-panel]'),
        panelRows: this.querySelector('[data-eps-panel-rows]'),
        panelTitle: this.querySelector('[data-eps-panel-title]'),
        panelSwatch: this.querySelector('[data-eps-panel-swatch]'),
        panelSum: this.querySelector('[data-eps-panel-sum]'),
        scrim: this.querySelector('[data-eps-scrim]'),
        panelDrag: this.querySelector('[data-eps-panel-drag]'),
        tilesView: this.querySelector('[data-eps-tiles-view]'),
        formView: this.querySelector('[data-eps-form-view]'),
        form: this.querySelector('[data-eps-form]'),
        selection: this.querySelector('[data-eps-selection]'),
        table: this.querySelector('[data-eps-table]'),
        empty: this.querySelector('[data-eps-empty]'),
        clear: this.querySelector('[data-eps-clear]'),
        bar: this.querySelector('[data-eps-bar]'),
        barCount: this.querySelector('[data-eps-count]'),
        barVolume: this.querySelector('[data-eps-volume]'),
        barTotal: this.querySelector('[data-eps-bar-total]'),
        status: this.querySelector('[data-eps-status]'),
        addButtons: Array.from(this.querySelectorAll('[data-eps-add]')),
        addLabel: this.querySelector('[data-eps-add-label]'),
        views: Array.from(this.querySelectorAll('[data-eps-view]')),
      };
      this.addLabel = this.els.addLabel.textContent;
      this.updateLabel = this.dataset.updateLabel || 'Update cart';
      this.restingLabel = this.dataset.restingLabel || 'In your cart';

      this.buildForm();

      this.addEventListener('click', this.onClick.bind(this));
      const onQty = this.onQty.bind(this);
      this.addEventListener('input', onQty);
      this.addEventListener('change', onQty);

      this.overlay = JLL.overlay(this, {
        panel: this.els.panel,
        scrim: this.els.scrim,
        drag: this.els.panelDrag,
        close: '[data-eps-panel-close]',
        ignore: '[data-eps-panel-close]',
        anchor: () => {
          const c = this.colorByName.get(this.openColor);
          return c && (c.tile.querySelector('.eps-tile__swatch') || c.tile);
        },
        arrow: this.els.panel.querySelector('.jll-arrow'),
        gap: 14,
        flip: true,
        keyboard: true,
        trap: true,
        /* A tile click is never "outside": it opens, switches or toggles. */
        inside: (t) => t.closest && t.closest('[data-eps-tile]') && this.contains(t),
        onClose: (returnFocus) => {
          const prev = this.openColor;
          this.openColor = null;
          this.tiles.forEach((t) => t.setAttribute('aria-expanded', 'false'));
          if (returnFocus) this.colorByName.get(prev).tile.focus();
        },
      });
      this.setView(this.dataset.defaultView === 'form' ? 'form' : 'tiles');

      /* First paint from the page's own render of the cart (the /cart.js shape): no
         request, no flash. The Back button and a foreign cart change re-read it. */
      const cart = this.querySelector('[data-eps-cart]');
      this.hydrate(cart ? JSON.parse(cart.textContent) : [], false);

      this.onPageShow = (e) => { if (e.persisted) this.readCart(true); };
      window.addEventListener('pageshow', this.onPageShow);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, (e) => {
          if (!e || e.source !== 'edge-paint-selector') this.readCart(true);
        });
      }
    }

    disconnectedCallback() {
      this.overlay.destroy();
      window.removeEventListener('pageshow', this.onPageShow);
      if (this.unsubscribeCart) this.unsubscribeCart();
    }

    /* ---------- state: one record per variant, { have, want, key } ---------- */

    getQty(id) {
      const line = this.list.get(id);
      return line ? line.want : 0;
    }

    /* A held line is priced as the cart prices it (final_price, discounts in);
       anything else at the catalogue price. */
    priceOf(id) {
      const line = this.list.get(id);
      return line && line.unit != null ? line.unit : this.byId.get(id).price;
    }

    maxFor(variant) {
      if (!variant || !variant.available) return 0;
      return variant.max === null || variant.max === undefined ? Infinity : Math.max(0, variant.max);
    }

    setQty(id, value) {
      const variant = this.byId.get(id);
      if (!variant) return;
      let q = parseInt(value, 10);
      if (isNaN(q) || q < 0) q = 0;
      const max = this.maxFor(variant);
      if (q > max) q = max === Infinity ? q : max;
      this.setWant(id, q);
      this.render();
    }

    /* A record lives while the cart holds the line or the list wants it. */
    setWant(id, want) {
      const line = this.list.get(id) || { have: 0, want: 0, key: null, unit: null };
      line.want = want;
      if (line.have || line.want) this.list.set(id, line);
      else this.list.delete(id);
    }

    items() {
      return Array.from(this.list, ([id, l]) => ({ id, quantity: l.want })).filter((i) => i.quantity > 0);
    }

    /* The list differs from the cart: the one fact the button and Undo answer to. */
    dirty() {
      return Array.from(this.list.values()).some((l) => l.want !== l.have);
    }

    /* The cart holds any of this product: the button reads Update, not Add. */
    held() {
      return Array.from(this.list.values()).some((l) => l.have > 0);
    }

    /* Undo: the list becomes the cart again — a clear while the cart holds nothing. */
    revert() {
      this.list.forEach((l, id) => this.setWant(id, l.have));
      this.render();
    }

    /* ---------- the cart ---------- */

    /* `items` in the Ajax cart shape. Ours: this product's plain lines (no properties,
       not nested under another product). Written past the stock clamp, so a held line
       is never dropped. keepEdits keeps unsent changes (a rebase); without it the list
       becomes the cart. */
    hydrate(items, keepEdits) {
      const inCart = new Map();
      items.forEach((l) => {
        const nested = l.parent_relationship && l.parent_relationship.parent_key;
        const propped = l.properties && Object.keys(l.properties).length;
        if (!this.byId.has(l.variant_id) || inCart.has(l.variant_id) || nested || propped) return;
        inCart.set(l.variant_id, l);
      });
      new Set([...this.list.keys(), ...inCart.keys()]).forEach((id) => {
        const line = this.list.get(id) || { have: 0, want: 0, key: null, unit: null };
        const edited = keepEdits && line.want !== line.have;
        const now = inCart.get(id);
        line.have = now ? now.quantity : 0;
        line.key = now ? now.key : null;
        line.unit = now && now.final_price != null ? now.final_price : null;
        if (!edited) line.want = line.have;
        if (line.have || line.want) this.list.set(id, line);
        else this.list.delete(id);
      });
      this.render();
    }

    async readCart(keepEdits) {
      try {
        const res = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
        if (res.ok) this.hydrate((await res.json()).items, keepEdits);
      } catch (e) {
        /* Offline: the page keeps what it has. */
      }
    }

    /* Pure. New lines are a /cart/add; changed and removed lines a /cart/update keyed
       by LINE KEY, never variant id (which would match a nested add-on line first). */
    static diff(list) {
      const adds = [];
      const updates = {};
      list.forEach((l, id) => {
        if (l.want === l.have) return;
        if (l.have) updates[l.key] = l.want;
        else adds.push({ id, quantity: l.want });
      });
      return { adds, updates };
    }

    lines() {
      const rows = [];
      this.colors.forEach((c) => {
        this.sizes.forEach((s) => {
          const v = this.byKey.get(keyOf(c.name, s));
          if (v && this.getQty(v.id) > 0) rows.push({ color: c, size: s, variant: v, qty: this.getQty(v.id) });
        });
      });
      return rows;
    }

    totals() {
      const items = this.items();
      const count = items.reduce((n, i) => n + i.quantity, 0);
      const total = items.reduce((n, i) => n + i.quantity * this.priceOf(i.id), 0);
      let volume = null;
      let unit = null;
      let ok = items.length > 0;
      items.forEach((i) => {
        const v = this.byId.get(i.id);
        const p = parseVolume(v.size);
        if (!p || (unit && p.u !== unit)) { ok = false; return; }
        unit = p.u;
        volume = (volume || 0) + p.n * i.quantity;
      });
      return { items, count, total, volume: ok ? formatVolume(volume, unit) : null };
    }

    /* ---------- markup helpers ---------- */

    stepper(variant, labelText) {
      const id = variant.id;
      const q = this.getQty(id);
      const max = this.maxFor(variant);
      const disabled = max === 0;
      const atMax = q >= max;
      return `
        <div class="eps-qty jll-stepper${disabled ? ' is-disabled' : ''}" data-eps-qty="${id}">
          <button type="button" data-eps-dec="${id}" aria-label="Decrease quantity, ${escapeHtml(labelText)}"${q === 0 || disabled ? ' disabled' : ''}>&minus;</button>
          <input class="eps-qty__input jll-stepper__value" type="number" inputmode="numeric" min="0"${max !== Infinity ? ` max="${max}"` : ''} value="${q}" data-eps-input="${id}" aria-label="Quantity, ${escapeHtml(labelText)}"${disabled ? ' disabled' : ''}>
          <button type="button" data-eps-inc="${id}" aria-label="Increase quantity, ${escapeHtml(labelText)}"${atMax || disabled ? ' disabled' : ''}>+</button>
        </div>`;
    }

    stockNote(variant) {
      if (!variant) return 'Not offered';
      if (!variant.available) return 'Sold out';
      const max = this.maxFor(variant);
      if (max !== Infinity && this.lowStock > 0 && max <= this.lowStock) return `Only ${max} left`;
      return '';
    }

    // Sizes sold out for a colour that is not entirely sold out, e.g. "120ml sold out".
    tileNote(colorName) {
      const out = [];
      let any = false;
      this.sizes.forEach((s) => {
        const v = this.byKey.get(keyOf(colorName, s));
        if (!v) return;
        if (v.available) any = true;
        else out.push(s);
      });
      if (!any || !out.length) return '';
      return `${out.join(', ')} sold out`;
    }

    /* ---------- the panel: the library's overlay ---------- */

    openPanel(colorName) {
      const color = this.colorByName.get(colorName);
      if (!color) return;
      this.openColor = colorName;
      this.tiles.forEach((t) => t.setAttribute('aria-expanded', t.dataset.color === colorName ? 'true' : 'false'));
      const { name, code } = splitName(colorName);
      this.els.panelTitle.textContent = code ? `${name} \u00b7 ${code}` : name;
      this.els.panelSwatch.style.setProperty('--eps-swatch', color.swatch);
      this.renderPanelRows();
      this.overlay.open();
      this.els.panel.focus({ preventScroll: true });
      this.scrollPanelIntoView();
    }

    closePanel(returnFocus) {
      this.overlay.close(returnFocus);
    }

    scrollPanelIntoView() {
      if (MOBILE.matches) return;
      const r = this.els.panel.getBoundingClientRect();
      const pad = 16;
      if (r.bottom > window.innerHeight - pad) {
        window.scrollBy({ top: r.bottom - window.innerHeight + pad, behavior: 'smooth' });
      } else if (r.top < pad + 80) {
        window.scrollBy({ top: r.top - pad - 80, behavior: 'smooth' });
      }
    }

    renderPanelRows() {
      if (!this.openColor) return;
      const color = this.openColor;
      this.els.panelRows.innerHTML = this.sizes
        .map((size) => {
          const v = this.byKey.get(keyOf(color, size));
          const note = this.stockNote(v);
          const unavailable = !v || !v.available;
          const sizeLabel = size === 'Default' ? 'Quantity' : size;
          return `
            <div class="eps-panel__row${unavailable ? ' is-unavailable' : ''}">
              <span class="eps-panel__size">${escapeHtml(sizeLabel)}</span>
              <span class="eps-panel__price">${v ? this.formatMoney(this.priceOf(v.id)) : ''}</span>
              ${v ? this.stepper(v, `${color} ${sizeLabel}`) : '<span></span>'}
              <span class="eps-panel__note">${escapeHtml(note)}</span>
            </div>`;
        })
        .join('');
      this.renderPanelSum();
    }

    renderPanelSum() {
      if (!this.openColor || !this.els.panelSum) return;
      let n = 0;
      let cents = 0;
      this.sizes.forEach((s) => {
        const v = this.byKey.get(keyOf(this.openColor, s));
        if (!v) return;
        const q = this.getQty(v.id);
        n += q;
        cents += q * this.priceOf(v.id);
      });
      this.els.panelSum.textContent = n ? `${n} ${n === 1 ? 'item' : 'items'} \u00b7 ${this.formatMoney(cents)}` : 'Nothing selected yet';
    }

    /* ---------- order form ---------- */

    buildForm() {
      if (!this.els.form) return;
      const head = `
        <thead>
          <tr>
            <th class="eps-form__color" scope="col">${escapeHtml(this.itemLabel)}</th>
            ${this.sizes
              .map((s) => {
                const sample = this.variants.find((v) => v.size === s);
                const price = sample ? `<small>${this.formatMoney(sample.price)}</small>` : '';
                return `<th scope="col">${escapeHtml(s === 'Default' ? 'Quantity' : s)}${price}</th>`;
              })
              .join('')}
          </tr>
        </thead>`;
      let currentFamily = null;
      const body = this.colors
        .map((c) => {
          const { name, code } = splitName(c.name);
          let familyRow = '';
          if (c.family && c.family !== currentFamily) {
            currentFamily = c.family;
            familyRow = `<tr class="eps-form__family"><td class="jll-eyebrow" colspan="${this.sizes.length + 1}">${escapeHtml(c.family)}</td></tr>`;
          }
          const cells = this.sizes
            .map((s) => {
              const v = this.byKey.get(keyOf(c.name, s));
              if (!v) return '<td>&mdash;</td>';
              const max = this.maxFor(v);
              return `<td><input class="eps-qty__input jll-field" type="number" inputmode="numeric" min="0"${
                max !== Infinity ? ` max="${max}"` : ''
              } value="${this.getQty(v.id)}" data-eps-input="${v.id}" aria-label="Quantity, ${escapeHtml(c.name)} ${escapeHtml(s)}"${
                max === 0 ? ' disabled' : ''
              }></td>`;
            })
            .join('');
          return `${familyRow}<tr>
            <th class="eps-form__color" scope="row">
              <span class="eps-swatch" style="--eps-swatch: ${escapeHtml(c.swatch)}"></span>${escapeHtml(name)}${
            code ? `<span class="eps-form__code">${escapeHtml(code)}</span>` : ''
          }
            </th>${cells}</tr>`;
        })
        .join('');
      this.els.form.innerHTML = `${head}<tbody>${body}</tbody>`;
    }

    setView(view) {
      this.view = view;
      this.els.views.forEach((b) => b.setAttribute('aria-pressed', b.dataset.epsView === view ? 'true' : 'false'));
      if (this.els.tilesView) this.els.tilesView.hidden = view !== 'tiles';
      if (this.els.formView) this.els.formView.hidden = view !== 'form';
      if (view !== 'tiles') this.closePanel(false);
    }

    /* ---------- render ---------- */

    render() {
      const { items, count, total, volume } = this.totals();

      this.colors.forEach((c) => {
        const badge = c.tile.querySelector('[data-eps-badge]');
        const note = c.tile.querySelector('[data-eps-note]');
        const n = this.sizes.reduce((sum, s) => {
          const v = this.byKey.get(keyOf(c.name, s));
          return sum + (v ? this.getQty(v.id) : 0);
        }, 0);
        if (badge) {
          badge.hidden = n === 0;
          badge.textContent = n;
        }
        c.tile.classList.toggle('is-selected', n > 0);
        if (note) {
          const text = this.tileNote(c.name);
          note.hidden = !text;
          note.textContent = text;
        }
      });

      const active = document.activeElement;
      this.querySelectorAll('[data-eps-input]').forEach((input) => {
        const id = parseInt(input.dataset.epsInput, 10);
        if (input !== active) input.value = this.getQty(id);
        const v = this.byId.get(id);
        const max = this.maxFor(v);
        const wrap = input.closest('[data-eps-qty]');
        if (wrap) {
          const q = this.getQty(id);
          wrap.querySelector('[data-eps-dec]').disabled = q === 0 || max === 0;
          wrap.querySelector('[data-eps-inc]').disabled = q >= max || max === 0;
        }
      });

      this.renderTable();

      /* Visibility follows the rows actually shown, not the total: a field
         cleared mid-edit reads zero while the row is still held, and hiding
         the list would blur the field under the cursor and rebuild it under
         the typist (docs/components/edge-paint-selector.md). */
      const has = count > 0;
      const hasRows = !!this.els.table.querySelector('[data-eps-row]');
      this.els.table.hidden = !hasRows;
      this.els.empty.hidden = hasRows;

      /* dirty: the list differs from the cart. held: the cart holds any of this product.
         The bar stays while a removal is staged, so it can be sent. The summary counts
         the list and nothing else — the whole cart's count is Shopify's to show — and
         the button carries the state: Add, Update, or resting as "In your cart". */
      const dirty = this.dirty();
      const held = this.held();
      this.els.clear.hidden = !dirty;
      this.els.clear.textContent = held ? 'Undo changes' : 'Clear all';
      this.els.bar.hidden = !has && !dirty;

      const lineCount = items.length;
      this.els.barCount.textContent = has
        ? `${count} ${count === 1 ? 'item' : 'items'} \u00b7 ${lineCount} ${lineCount === 1 ? 'line' : 'lines'}`
        : 'Nothing selected';
      this.els.barVolume.textContent = volume ? `\u00b7 ${volume} total` : '';
      this.els.barTotal.textContent = this.formatMoney(total);
      this.els.addLabel.textContent = !held ? this.addLabel : dirty ? this.updateLabel : this.restingLabel;
      this.els.addButtons.forEach((b) => (b.disabled = !dirty || this.loading === true));

      this.renderPanelSum();
    }

    /* Rebuilt ONLY when a row appears or disappears; a quantity change is
       rewritten in place so the stepper under the finger survives. A focused
       field cleared to retype must not rebuild either — the blur's change
       event does that (docs/components/edge-paint-selector.md). */
    renderTable() {
      const rows = this.lines();
      const key = rows.map((r) => r.variant.id).join(',');
      const active = document.activeElement;
      const typing = !!active && active.matches('[data-eps-input]') && this.els.table.contains(active);

      if (key !== this.tableKey && !typing) {
        this.els.table.innerHTML = rows
          .map((r) => {
            const { name, code } = splitName(r.color.name);
            return `
              <div class="eps-row" data-eps-row="${r.variant.id}">
                <span class="eps-swatch" style="--eps-swatch: ${escapeHtml(r.color.swatch)}"></span>
                <span class="eps-row__name">${escapeHtml(name)}${code ? `<span class="eps-row__code">${escapeHtml(code)}</span>` : ''}</span>
                <span class="eps-row__size">${escapeHtml(r.size === 'Default' ? '' : r.size)}</span>
                ${this.stepper(r.variant, `${r.color.name} ${r.size}`)}
                <span class="eps-row__line">${this.formatMoney(r.qty * this.priceOf(r.variant.id))}</span>
                <button type="button" class="eps-row__remove" data-eps-remove="${r.variant.id}" aria-label="Remove ${escapeHtml(r.color.name)} ${escapeHtml(r.size)}">&times;</button>
              </div>`;
          })
          .join('');
        this.tableKey = key;
        return;
      }

      /* Walk the DOM, not `rows`: a row mid-edit may already have left the
         selection, and it still has to show an honest line total. */
      this.els.table.querySelectorAll('[data-eps-row]').forEach((row) => {
        const variant = this.byId.get(parseInt(row.dataset.epsRow, 10));
        const line = row.querySelector('.eps-row__line');
        if (variant && line) line.textContent = this.formatMoney(this.getQty(variant.id) * this.priceOf(variant.id));
      });
    }

    /* ---------- events ---------- */

    onClick(e) {
      const t = e.target;

      const tile = t.closest('[data-eps-tile]');
      if (tile) {
        this.clearStatus();
        if (this.openColor === tile.dataset.color) this.closePanel(false);
        else this.openPanel(tile.dataset.color);
        return;
      }
      const inc = t.closest('[data-eps-inc]');
      if (inc) return this.setQty(parseInt(inc.dataset.epsInc, 10), this.getQty(parseInt(inc.dataset.epsInc, 10)) + 1);
      const dec = t.closest('[data-eps-dec]');
      if (dec) return this.setQty(parseInt(dec.dataset.epsDec, 10), this.getQty(parseInt(dec.dataset.epsDec, 10)) - 1);
      const remove = t.closest('[data-eps-remove]');
      if (remove) return this.setQty(parseInt(remove.dataset.epsRemove, 10), 0);

      if (t.closest('[data-eps-clear]')) {
        this.clearStatus();
        return this.revert();
      }
      const view = t.closest('[data-eps-view]');
      if (view) return this.setView(view.dataset.epsView);

      if (t.closest('[data-eps-bar-summary]')) {
        return this.els.selection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      if (t.closest('[data-eps-add]')) return this.apply();
    }

    /* Bound to both input and change. They do the same thing now; change still
       matters because it fires on blur, which is when a row emptied mid-edit
       finally gets cleared out. */
    onQty(e) {
      const input = e.target.closest('[data-eps-input]');
      if (!input) return;
      this.setQty(parseInt(input.dataset.epsInput, 10), input.value);
    }

    /* ---------- the one button ---------- */

    /* Adds first, so the cart is never empty mid-way. An add ends in Dawn's popup, as on
       every other product; a pure update, which has no popup, in the bubble and one sentence. */
    async apply() {
      const { adds, updates } = EdgePaintSelector.diff(this.list);
      if ((!adds.length && !Object.keys(updates).length) || this.loading) return;
      this.loading = true;
      /* Dawn's own button loading state: the label goes transparent, the spinner shows. */
      this.els.addButtons.forEach((b) => {
        b.disabled = true;
        b.classList.add('loading');
        b.querySelector('.loading__spinner').classList.remove('hidden');
        b.setAttribute('aria-busy', 'true');
      });
      this.clearStatus();

      /* Dawn's own add flow: the notification (or drawer) names the sections
         it re-renders and draws itself from the answer. */
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const sections = cart ? cart.getSectionsToRender().map((s) => s.id) : [];
      const post = (url, body) => JLL.cart(url, Object.assign(body, { sections, sections_url: window.location.pathname }));
      try {
        const added = adds.length ? await post(routes.cart_add_url, { items: adds }) : null;
        const updated = Object.keys(updates).length ? await post(routes.cart_update_url, { updates }) : null;
        const last = updated || added;
        if (added && cart) {
          /* The popup draws from the LAST answer, so its "View cart (N)" and the bubble agree;
             the items form answers without the top-level key it renders (platform fact 24). */
          last.key = added.items[0].key;
          cart.renderContents(last);
        } else {
          const bubble = document.getElementById('cart-icon-bubble');
          if (cart && bubble && last.sections && last.sections['cart-icon-bubble']) {
            bubble.innerHTML = cart.getSectionInnerHTML(last.sections['cart-icon-bubble']);
          }
          this.setStatus(`Cart updated. <a class="jll-link" href="${escapeHtml(routes.cart_url)}">View cart</a>`, 'ok');
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS === 'object') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'edge-paint-selector', cartData: last });
        }
        this.closePanel(false);
      } catch (err) {
        this.setStatus(escapeHtml(err.message || 'Something went wrong. Please try again.'), 'error');
      } finally {
        await this.readCart(false);
        this.loading = false;
        this.els.addButtons.forEach((b) => {
          b.classList.remove('loading');
          b.querySelector('.loading__spinner').classList.add('hidden');
          b.removeAttribute('aria-busy');
        });
        this.render();
      }
    }

    setStatus(html, kind) {
      this.els.status.className = kind === 'error' ? 'eps-status jll-status jll-status--error' : 'eps-status jll-status';
      this.els.status.innerHTML = html;
      if (kind === 'ok') this.els.selection.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    clearStatus() {
      this.els.status.className = 'eps-status jll-status';
      this.els.status.innerHTML = '';
    }
  }

  customElements.define('edge-paint-selector', EdgePaintSelector);
})();
